import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './ServicesSettings.css';
import {
  LUCIDE_SERVICE_ICON_OPTIONS,
  normalizeServiceIconKey,
} from '../../constants/lucideServiceSectionIcons';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const briefcaseOption = LUCIDE_SERVICE_ICON_OPTIONS.find(
  (o) => o.value === 'briefcase'
);

function selectedIconOption(iconKey) {
  const k = normalizeServiceIconKey(iconKey);
  return (
    LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === k) || briefcaseOption
  );
}

const iconSelectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: 'rgba(1, 7, 39, 0.8)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
    minHeight: 38,
    boxShadow: 'none',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.35)' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#051139',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    zIndex: 20,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: 280, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(255, 255, 255, 0.12)'
      : 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 13,
  }),
  singleValue: (base) => ({ ...base, color: '#ffffff' }),
  input: (base) => ({ ...base, color: '#ffffff' }),
  placeholder: (base) => ({ ...base, color: 'rgba(246, 246, 246, 0.55)' }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(246, 246, 246, 0.75)',
  }),
};

const emptyCard = () => ({
  id: Date.now().toString(),
  title: '',
  summary: '',
  iconKey: 'briefcase',
  targetSlug: 'companies-establishment',
});

function ServicesSettings() {
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/services/section-cards`
        );
        if (Array.isArray(res.data?.cards)) {
          setCards(res.data.cards);
        } else {
          setCards([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, []);

  const handleChange = (index, field, value) => {
    setCards((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAdd = () => {
    setCards((prev) => [...prev, emptyCard()]);
  };

  const handleDelete = (index) => {
    setCards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const payload = cards.map((c) => ({
        ...c,
        iconKey: normalizeServiceIconKey(c.iconKey),
      }));
      const res = await axios.put(
        `${API_BASE_URL}/api/services/section-cards`,
        payload
      );
      if (Array.isArray(res.data?.cards)) {
        setCards(res.data.cards);
      }
      setMessage('Services section updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save services. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="services-settings-loading">Loading services…</p>;
  }

  return (
    <div className="services-settings">
      <div className="services-settings-header">
        <div>
          <h2 className="services-settings-title">Home services section</h2>
          <p className="services-settings-subtitle">
            Manage the service cards shown on the home page and link them to sections
            on the Services page.
          </p>
        </div>
        <div className="services-settings-actions">
          <button
            type="button"
            className="services-settings-secondary-btn"
            onClick={handleAdd}
          >
            + Add service card
          </button>
          <button
            type="button"
            className="services-settings-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="services-settings-list">
        {cards.map((card, index) => (
          <div key={card.id || index} className="services-settings-row">
            <div className="services-settings-col services-settings-col-main">
              <label className="services-settings-label">
                Title
                <input
                  type="text"
                  className="services-settings-input"
                  value={card.title}
                  onChange={(e) =>
                    handleChange(index, 'title', e.target.value)
                  }
                  placeholder="Company Formation and Licensing"
                />
              </label>

              <label className="services-settings-label">
                Summary
                <textarea
                  className="services-settings-textarea"
                  rows={2}
                  value={card.summary}
                  onChange={(e) =>
                    handleChange(index, 'summary', e.target.value)
                  }
                  placeholder="Short one‑line description under the title."
                />
              </label>

              <label className="services-settings-label">
                Target ServicesPage hash (slug)
                <input
                  type="text"
                  className="services-settings-input"
                  value={card.targetSlug}
                  onChange={(e) =>
                    handleChange(index, 'targetSlug', e.target.value)
                  }
                  placeholder="companies-establishment"
                />
              </label>
            </div>

            <div className="services-settings-col services-settings-col-icon">
              <label className="services-settings-label">
                Icon
                <Select
                  className="services-settings-icon-select"
                  classNamePrefix="services-settings-icon-select"
                  options={LUCIDE_SERVICE_ICON_OPTIONS}
                  value={selectedIconOption(card.iconKey)}
                  onChange={(opt) =>
                    handleChange(index, 'iconKey', opt?.value || 'briefcase')
                  }
                  isSearchable
                  placeholder="Search icons…"
                  menuPlacement="auto"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={iconSelectStyles}
                  formatOptionLabel={(opt) => (
                    <span title={opt.value}>
                      {opt.label}{' '}
                      <span className="services-settings-icon-slug">
                        ({opt.value})
                      </span>
                    </span>
                  )}
                />
              </label>

              <button
                type="button"
                className="services-settings-delete-btn"
                onClick={() => handleDelete(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {cards.length === 0 && (
          <p className="services-settings-empty">
            No service cards configured yet. Click “Add service card” to create one.
          </p>
        )}
      </div>

      {message && <p className="services-settings-message">{message}</p>}
    </div>
  );
}

export default ServicesSettings;

